import chalk from "chalk";
import { CUSTOM_FILTER_SORT_ORDER } from "@/features/sub-families/resources/constants.ts";
import {
    type AllowedCombinationKey,
    type BaseProperty,
    type ChangeValue,
    type Combination,
    type CombinationKey,
    type CurrentOptions,
    Difference,
    type DifferenceEnum,
    type Differences,
    type Product,
    type ProductData,
    type ProductPropertyKey,
    type ProductReference,
    type Properties,
    type Property,
    type PropertyConfigCode,
    type SmartNumberDescription,
    type SmartNumberMapping,
} from "@/features/sub-families/types";
import {
    getPowerSupplyOptionsByProductScenario,
    getProductScenario,
} from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import { getEmptyConfigCode } from "@/features/sub-families/utils/smart-number.ts";
import type { TranslateFn } from "@/features/translations/types";
import Logger from "@/lib/logger.ts";
import { getCombinationFromProduct, getProductPropertyByDescriptor } from "@/lib/product.ts";
import { getAccessoryByConfigCode } from "@/services/product/utils/accessories.ts";
import { getMountingKitByConfigCode } from "@/services/product/utils/mounting-kits.ts";
import { type Nullable, SmartNumberDescriptor, type SmartNumberDescriptorKey } from "@/types";
import { sortByCustomOrder } from "@/utils/sort.ts";
import { typedObjectEntries, typedObjectKeys } from "@/utils/types.ts";

// Only scalar Property keys are allowed here; array-valued ones like light_acc,
// ceiling_meta_data and supply don't have a single configCode, so they can't be
// compared this way. Adding them would fail to compile.
type PreservableProperty = {
    [K in ProductPropertyKey]: Properties[K] extends Property<unknown> ? K : never;
}[ProductPropertyKey];

// Scalar product properties we try to keep stable when swapping products,
// unless the user is explicitly changing them. Combination properties
// (cct, cri, beam_angle, dimming_type/supply) are handled by getCombinationScore,
// so they don't belong here. ledchip & light_distributor are intentionally
// excluded: they're driven by cct/cri, not independently selectable.
const PRESERVABLE_PROPERTIES: PreservableProperty[] = ["dimension"];

export const getCombinationKeys = (product: Nullable<Product>) => {
    const combinationKeys: CombinationKey[] = ["cct", "cri", "beam_angle"];

    const productScenario = getProductScenario(product);
    switch (productScenario) {
        case 1:
        case 4:
            combinationKeys.push("dimming_type");
            break;

        case 2:
            combinationKeys.push("supply");
            break;

        default:
            break;
    }

    return combinationKeys;
};

export function canUpdateWithoutCheckingCombinations(product: Nullable<Product>, descriptor: SmartNumberDescriptorKey) {
    const productProperty = getProductPropertyByDescriptor(descriptor);
    const combinationKeys = getCombinationKeys(product);

    return !combinationKeys.includes(productProperty as CombinationKey);
}

export function findBestMatchingProduct(
    currentProduct: Nullable<Product>,
    change: ChangeValue,
    products: ProductData,
    smartNumberMapping: SmartNumberMapping,
): Nullable<[ProductReference, Product]> {
    const bestMatchingProductReference = findBestMatchingProductReference(
        currentProduct,
        change,
        products,
        smartNumberMapping,
    );

    // This should never happen as we are selection an option that is calculated from thew properties of a product
    // So there should always be - at least - 1 product for the chosen property.
    if (!bestMatchingProductReference) return null;

    const product = products[bestMatchingProductReference];

    return product ? [bestMatchingProductReference, product] : null;
}

export function getDifferences(
    currentProduct: Nullable<Product>,
    bestMatchingProduct: Nullable<Product>,
    smartNumberMapping: SmartNumberMapping,
    smartNumberDescription: SmartNumberDescription,
    descriptor: SmartNumberDescriptorKey,
) {
    const changingProperty = getProductPropertyByDescriptor(descriptor);

    const currentOptions: CurrentOptions = {
        accessory: smartNumberMapping[SmartNumberDescriptor.Accessory],
        mountingKit: smartNumberMapping[SmartNumberDescriptor.MountingKit],
        powerSupply: smartNumberMapping[SmartNumberDescriptor.PowerSupply],
    };

    const currentDifferences = findDifferences(
        currentProduct,
        bestMatchingProduct,
        changingProperty,
        currentOptions,
        smartNumberDescription,
        smartNumberMapping,
    );

    // We do not need to display the massCode when this possibly changes, it isn't displayed in the configurator anyway
    return currentDifferences?.filter((diff) => diff.property !== "massCode") ?? [];
}

export function getProperty(
    property: ProductPropertyKey,
    product: Nullable<Product>,
    currentConfigCode: Nullable<PropertyConfigCode>,
): Nullable<BaseProperty> {
    if (!product) return null;

    const productProperty = product?.properties[property] ?? null;
    if (!productProperty) return null;

    if (!Array.isArray(productProperty)) return productProperty as Property;

    switch (property) {
        case "ceiling_meta_data": {
            const currentMountingKit = getMountingKitByConfigCode(product, currentConfigCode);

            if (!currentMountingKit) return null;

            return { value: [currentMountingKit.ceilingLabel], unit: null, name: "mounting kit" };
        }

        case "light_acc": {
            const currentAccessory = getAccessoryByConfigCode(product, currentConfigCode);

            if (!currentAccessory) return null;

            return { value: [currentAccessory.title], unit: null, name: "accessory" };
        }

        default: {
            Logger.warn("property", chalk.cyan(property), "not supported");
            return null;
        }
    }
}

export function formatDifference(difference: DifferenceEnum, propertyName: string, t: TranslateFn) {
    return difference === Difference.Change ? propertyName : t("change_feature_removed", { property: propertyName });
}

function findBestMatchingProductReference(
    currentProduct: Nullable<Product>,
    change: ChangeValue,
    products: ProductData,
    smartNumberMapping: SmartNumberMapping,
) {
    if (!currentProduct) return null;

    const currentProductCombination = getCombinationFromProduct(currentProduct, smartNumberMapping);
    const combinationKeys = typedObjectKeys(currentProductCombination);
    const changeProperty = getProductPropertyByDescriptor(change.descriptor);

    const allowedCombinationKey = changeProperty as AllowedCombinationKey;

    // create a property that should ALWAYS MATCH with the combinations of another product
    const importantProperty =
        changeProperty !== null && combinationKeys.includes(allowedCombinationKey) ? allowedCombinationKey : null;

    // Combinations must always dominate the preserved-properties tie-breaker.
    // Weighting by (preservable count + 1) guarantees one combination match
    // outweighs any number of preserved properties, no matter how the list grows.
    const COMBINATION_WEIGHT = PRESERVABLE_PROPERTIES.length + 1;

    const result = typedObjectEntries(products).reduce<{ reference: ProductReference; score: number }>(
        (acc, [reference, product]) => {
            const productCombination = getCombinationFromProduct(product, smartNumberMapping);
            const combinationScore = getCombinationScore(
                currentProductCombination,
                productCombination,
                importantProperty,
                change.configCode,
            );

            // -1 means the candidate doesn't satisfy the requested change; skip it.
            if (combinationScore < 0) return acc;

            // Tie-breaker: among candidates with the same combination score,
            // prefer the ones that keep the most current scalar properties.
            const preservedCount = countPreservedProperties(currentProduct, product, changeProperty);
            const score = combinationScore * COMBINATION_WEIGHT + preservedCount;

            if (score > acc.score) {
                acc.score = score;
                acc.reference = reference;
            }

            return acc;
        },
        { score: -1, reference: "" as ProductReference },
    );

    if (result.score === -1) return null;

    return result.reference;
}

function findDifferences(
    currentProduct: Nullable<Product>,
    newProduct: Nullable<Product>,
    changingProperty: Nullable<ProductPropertyKey>,
    currentOptions: CurrentOptions,
    smartNumberDescription: SmartNumberDescription,
    smartNumberMapping: SmartNumberMapping,
): Nullable<Differences> {
    if (!currentProduct || !newProduct) return null;

    const currentProductCombination = getCombinationFromProduct(currentProduct, smartNumberMapping);
    const otherProductCombination = getCombinationFromProduct(newProduct, smartNumberMapping);

    const differences: Differences = [];
    for (const [key, value] of typedObjectEntries(currentProductCombination)) {
        if (key === changingProperty) continue; // This an intended difference as we're changing it

        if (otherProductCombination[key] !== value) {
            differences.push({ property: key as ProductPropertyKey, difference: Difference.Change });
        }
    }

    for (const [key, configCode] of typedObjectEntries(currentOptions)) {
        switch (key) {
            case "accessory": {
                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.Accessory, smartNumberDescription);
                const hasCurrentAccessory = getAccessoryByConfigCode(newProduct, configCode) !== null;

                if (!hasCurrentAccessory && configCode !== emptyConfigCode) {
                    differences.push({ property: "light_acc", difference: Difference.Remove });
                }

                break;
            }

            case "mountingKit": {
                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.MountingKit, smartNumberDescription);
                const hasCurrentMountingKit = getMountingKitByConfigCode(newProduct, configCode) !== null;

                if (!hasCurrentMountingKit && configCode !== emptyConfigCode) {
                    differences.push({ property: "ceiling_meta_data", difference: Difference.Remove });
                }

                break;
            }

            case "powerSupply": {
                const productScenario = getProductScenario(newProduct);
                if (productScenario === null) break; // Should not happen

                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, smartNumberDescription);
                const supplies = getPowerSupplyOptionsByProductScenario(productScenario, newProduct);
                const hasCurrentPowerSupply = supplies.some((supply) => supply.configCode === configCode);

                if (!hasCurrentPowerSupply && configCode !== emptyConfigCode) {
                    // Only add power_supply difference for scenario 2 or 3
                    if ([2, 3].includes(productScenario)) {
                        differences.push({ property: "power_supply", difference: Difference.Change });
                    }

                    differences.push({ property: "dimming_type", difference: Difference.Remove });
                }

                break;
            }

            default:
                break;
        }
    }

    return sortByCustomOrder(differences, "property", CUSTOM_FILTER_SORT_ORDER);
}

function getCombinationScore(
    currentProductCombination: Combination,
    combinationCandidate: Combination,
    importantProperty: Nullable<AllowedCombinationKey>,
    propertyValue: PropertyConfigCode,
) {
    if (importantProperty !== null && combinationCandidate[importantProperty] !== propertyValue) return -1;

    let count = 0;
    for (const [property, value] of typedObjectEntries(combinationCandidate)) {
        // Increase the count when the property results in the expected value
        if (currentProductCombination[property] === value) count++;
    }

    return count;
}

function countPreservedProperties(current: Product, candidate: Product, changeProperty: Nullable<ProductPropertyKey>) {
    let count = 0;
    for (const property of PRESERVABLE_PROPERTIES) {
        // Don't reward keeping the property the user is actively changing.
        if (property === changeProperty) continue;

        const currentValue = current.properties[property].configCode;
        const candidateValue = candidate.properties[property].configCode;
        if (currentValue === candidateValue) count++;
    }
    return count;
}
